import React, { useState, useEffect } from 'react';
import { Plus, Edit } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, STAGES } from './crm/constants';
import { ClientCard } from './crm/ClientCard';
import { ClientModal } from './crm/ClientModal';
import { ClientDetailModal } from './crm/ClientDetailModal';

export const CRMPipeline = ({ hubspotStatus }) => {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ pipeline: {}, total_clients: 0, pending_tasks: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState('pipeline');
  const [editingClient, setEditingClient] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, statsRes] = await Promise.all([
        axios.get(`${API}/crm/clients`, { withCredentials: true }),
        axios.get(`${API}/crm/stats`, { withCredentials: true }),
      ]);
      setClients(clientsRes.data);
      setStats(statsRes.data);
    } catch {
      console.error('Failed to fetch CRM data');
    } finally {
      setLoading(false);
    }
  };

  const moveToStage = async (clientId, newStage) => {
    try {
      await axios.put(`${API}/crm/clients/${clientId}`, { stage: newStage }, { withCredentials: true });
      toast.success(`Moved to ${newStage}`);
      fetchData();
    } catch {
      toast.error('Failed to update stage');
    }
  };

  const openClientDetail = (client) => {
    setSelectedClient(client);
    setShowDetailModal(true);
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setShowClientModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#C6A86A] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="crm-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl text-white">Client Pipeline</h2>
          <p className="text-white/60 text-sm mt-1">
            {stats.total_clients} clients • {stats.pending_tasks} pending tasks
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex bg-[#050505] border border-white/10 p-1">
            <button
              onClick={() => setViewMode('pipeline')}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                viewMode === 'pipeline' ? 'bg-[#D4AF37] text-white' : 'text-white/60'
              }`}
            >
              Pipeline
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                viewMode === 'list' ? 'bg-[#D4AF37] text-white' : 'text-white/60'
              }`}
            >
              List
            </button>
          </div>
          <button
            onClick={() => {
              setEditingClient(null);
              setShowClientModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors"
            data-testid="add-crm-client-btn"
          >
            <Plus size={16} /> Add Client
          </button>
        </div>
      </div>

      {viewMode === 'pipeline' ? (
        <PipelineView
          clients={clients}
          hubspotStatus={hubspotStatus}
          onCardClick={openClientDetail}
        />
      ) : (
        <ListView clients={clients} onRowClick={openClientDetail} onEditClick={openEditModal} />
      )}

      <ClientModal
        open={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setEditingClient(null);
        }}
        client={editingClient}
        onSave={fetchData}
      />

      <ClientDetailModal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onUpdate={fetchData}
        onMoveStage={moveToStage}
      />
    </div>
  );
};

// Pipeline kanban view
const PipelineView = ({ clients, hubspotStatus, onCardClick }) => (
  <div className="overflow-x-auto pb-4">
    <div className="flex gap-4 min-w-max">
      {STAGES.map((stage) => {
        const stageClients = clients.filter((c) => c.stage === stage.id);
        return (
          <div
            key={stage.id}
            className="w-72 flex-shrink-0"
            data-testid={`pipeline-stage-${stage.id}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-3 h-3 rounded-full ${stage.color}`} />
              <h3 className="text-white font-medium">{stage.label}</h3>
              <span className="text-white/40 text-sm">({stageClients.length})</span>
            </div>
            <div className="space-y-3 min-h-[200px] bg-[#0a0a0a] border border-white/10 p-3">
              {stageClients.map((client) => (
                <ClientCard
                  key={client.crm_id}
                  client={client}
                  hubspotStatus={hubspotStatus}
                  onClick={() => onCardClick(client)}
                />
              ))}
              {stageClients.length === 0 && (
                <p className="text-white/30 text-sm text-center py-8">No clients</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// List/table view
const ListView = ({ clients, onRowClick, onEditClick }) => (
  <div className="bg-[#0a0a0a] border border-white/10 overflow-hidden">
    <table className="w-full">
      <thead>
        <tr className="border-b border-white/10">
          {['Client', 'Event Type', 'Date', 'Budget', 'Stage'].map((h) => (
            <th key={h} className="text-left text-white/60 text-xs uppercase tracking-widest p-4">
              {h}
            </th>
          ))}
          <th className="text-right text-white/60 text-xs uppercase tracking-widest p-4">Actions</th>
        </tr>
      </thead>
      <tbody>
        {clients.map((client) => {
          const stage = STAGES.find((s) => s.id === client.stage);
          return (
            <tr
              key={client.crm_id}
              className="border-b border-white/5 hover:bg-[#050505] cursor-pointer"
              onClick={() => onRowClick(client)}
            >
              <td className="p-4">
                <p className="text-white font-medium">{client.name}</p>
                <p className="text-white/60 text-sm">{client.email}</p>
              </td>
              <td className="p-4 text-white/80">{client.event_type}</td>
              <td className="p-4 text-white/60">{client.event_date || '-'}</td>
              <td className="p-4 text-white/60">{client.budget_range || '-'}</td>
              <td className="p-4">
                <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 ${stage?.color}/20`}>
                  <span className={`w-2 h-2 rounded-full ${stage?.color}`} />
                  {stage?.label}
                </span>
              </td>
              <td className="p-4 text-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditClick(client);
                  }}
                  className="p-2 text-white/40 hover:text-white"
                >
                  <Edit size={16} />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
